// src/components/Sidebar.js

import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import {
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Drawer,
    Typography,
    Divider,
    useTheme,
    styled,
    Collapse,
    IconButton,
    CircularProgress,
} from '@mui/material';
import DashboardIcons from '@mui/icons-material/Dashboard';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import FolderIcon from '@mui/icons-material/Folder';
import ForumIcon from '@mui/icons-material/Forum';
import * as MuiIcons from '@mui/icons-material';
import { useChatbots } from '../contexts/ChatbotContext';
import UpgradePlan from './UpgradePlan';
import axiosInstance from '../utils/axiosInstance';

const SidebarHeaderButton = styled(ListItemButton)(({ theme }) => ({
    padding: theme.spacing(2, 2, 1, 2),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#ffffff', // ✅ full white
    fontSize: '0.9rem', // ✅ slightly bigger header
    justifyContent: 'space-between',
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)', // little brighter hover
    },
}));

const SidebarHeaderText = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#ffffff', // ✅ full white
    fontSize: '0.8rem', // ✅ slightly bigger
}));

const StyledListItemButton = styled(ListItemButton)(({ theme, active, disabled }) => ({
    '&:hover': {
        backgroundColor: !disabled ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
    },
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius * 1.5,
    margin: theme.spacing(0.5, 1),
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    ...(active && !disabled && {
        backgroundColor: 'rgba(59, 130, 246, 0.15)',
        '&:hover': {
            backgroundColor: 'rgba(59, 130, 246, 0.2)',
        },
        '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
            color: '#3b82f6',
        },
    }),
}));

const StyledListItemIcon = styled(ListItemIcon)(({ theme, active }) => ({
    color: active ? '#3b82f6' : 'rgba(255, 255, 255, 0.7)',
    minWidth: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const StyledListItemText = styled(ListItemText)(({ theme, active }) => ({
    '& .MuiTypography-root': {
        color: active ? '#3b82f6' : '#ffffff', // ✅ full white when not active
        fontSize: '1rem', // ✅ slightly larger menu font
        fontWeight: 500,
    },
}));

export default function Sidebar({ drawerWidth, isOpen, onClose, isMobile }) {
    const theme = useTheme();
    const { activeChatbot } = useChatbots();
    
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openSections, setOpenSections] = useState({
        chatbot: true,
    });

    const userStr = localStorage.getItem("user");
    const user = userStr ? JSON.parse(userStr) : null;
    const userRole = user?.active_team?.user_role_in_team || "";

    const isChatbotSetupComplete = activeChatbot ? activeChatbot.is_process_completed : false;
    const chatbotIdentity = activeChatbot ? activeChatbot.chatbot_identity : null;

    const getIconComponent = (iconName) => {
        if (!iconName) {
            console.log('No icon name provided, using FolderIcon');
            return FolderIcon;
        }
        
        console.log('Looking for icon:', iconName);
        const IconComponent = MuiIcons[iconName];
        
        if (!IconComponent) {
            console.log(`Icon "${iconName}" not found in MuiIcons, using FolderIcon`);
            return FolderIcon;
        }
        
        console.log(`Found icon: ${iconName}`);
        return IconComponent;
    };

    useEffect(() => {
        const fetchModules = async () => {
            try {
                setLoading(true);
                const response = await axiosInstance.get('/api/modules');
                
                if (response.data.success) {
                    const modulesData = JSON.parse(response.data.data).modules;
                    console.log('Fetched modules:', modulesData);
                    setModules(modulesData);
                    
                    const initialSections = { chatbot: true };
                    modulesData.forEach(module => {
                        initialSections[module.slug] = false;
                    });
                    setOpenSections(initialSections);
                }
            } catch (error) {
                console.error('Error fetching modules:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchModules();
    }, []);

    const getLinkProps = (isFeatureLink = true) => {
        if (isFeatureLink && !isChatbotSetupComplete) {
            return {
                onClick: (e) => e.preventDefault(),
            };
        }
        return {
            onClick: handleDrawerToggle,
        };
    };

    const handleSectionToggle = (section) => {
        setOpenSections(prevState => ({
            ...prevState,
            [section]: !prevState[section],
        }));
    };

    const handleDrawerToggle = () => {
        if (isMobile && onClose) {
            onClose();
        }
    };

   const drawerStyle = {
    width: drawerWidth,
    flexShrink: 0,
    '& .MuiDrawer-paper': {
        width: drawerWidth,
        boxSizing: 'border-box',
        backgroundColor: '#1e293b',
        color: '#ffffff',
        borderRight: 'none',
        borderBottomRightRadius: isMobile ? 0 : theme.shape.borderRadius * 2,
        borderTopRightRadius: isMobile ? 0 : theme.shape.borderRadius * 2,
        marginTop: 0,
        marginBottom: 0,
        height: '100%', // ✅ full height
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
            width: '6px',
        },
        '&::-webkit-scrollbar-track': {
            background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '3px',
            '&:hover': {
                background: 'rgba(255, 255, 255, 0.3)',
            },
        },
    },
};

    const drawerProps = {
        sx: drawerStyle,
        variant: isMobile ? 'temporary' : 'persistent',
        anchor: 'left',
        open: isOpen,
        onClose: onClose,
    };

    const renderModuleItem = (module, isSubmodule = false) => {
        const path = `chatbot/${chatbotIdentity}/${module.slug}`;
        const IconComponent = getIconComponent(module.icon);
        
        return (
            <ListItem key={module.id} disablePadding>
                <StyledListItemButton
                    component={NavLink}
                    to={path}
                    sx={{ pl: 4 }}
                    {...getLinkProps(true)}
                    disabled={!isChatbotSetupComplete}
                >
                    <StyledListItemIcon sx={{ minWidth: 40 }}>
                        <IconComponent />
                    </StyledListItemIcon>
                    <StyledListItemText primary={module.name} />
                </StyledListItemButton>
            </ListItem>
        );
    };

    const renderModuleSection = (module) => {
        const hasSubmodules = module.submodules && module.submodules.length > 0;
        
        return (
            <React.Fragment key={module.id}>
                <ListItem disablePadding>
                    <SidebarHeaderButton onClick={() => handleSectionToggle(module.slug)}>
                        <SidebarHeaderText>{module.name}</SidebarHeaderText>
                        <IconButton 
                            edge="end" 
                            size="small" 
                            aria-label={`toggle ${module.slug}`}
                            sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                        >
                            {openSections[module.slug] ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    </SidebarHeaderButton>
                </ListItem>
                <Collapse in={openSections[module.slug]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                        {hasSubmodules ? (
                            module.submodules.map(submodule => renderModuleItem(submodule, true))
                        ) : (
                            renderModuleItem(module, false)
                        )}
                    </List>
                </Collapse>
            </React.Fragment>
        );
    };

    return (
        <Drawer {...drawerProps}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', mt: isMobile ? 0 : 8 }}>
                <List component="nav" sx={{ flexGrow: 1 }}>
                    {/* Chatbots Section */}
                    <ListItem disablePadding>
                        <SidebarHeaderButton onClick={() => handleSectionToggle("chatbot")}>
                            <SidebarHeaderText>Chatbots</SidebarHeaderText>
                            <IconButton 
                                edge="end" 
                                size="small"
                                sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                            >
                                {openSections.chatbot ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                        </SidebarHeaderButton>
                    </ListItem>
                    <Collapse in={openSections.chatbot} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            <ListItem disablePadding>
                                <StyledListItemButton
                                    component={NavLink}
                                    to={`/my-chatbot`}
                                    sx={{ pl: 4 }}
                                    {...getLinkProps(false)}
                                >
                                    <StyledListItemIcon>
                                        <SmartToyIcon />
                                    </StyledListItemIcon>
                                    <StyledListItemText primary={userRole === "admin" ? "My Chatbots" : "Shared Chatbots"} />
                                </StyledListItemButton>
                            </ListItem>
                        </List>
                    </Collapse>

                    <ListItem disablePadding>
                        <SidebarHeaderButton onClick={() => handleSectionToggle('main')}>
                            <SidebarHeaderText>Statistics</SidebarHeaderText>
                            <IconButton 
                                edge="end" 
                                size="small" 
                                aria-label="toggle main"
                                sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                            >
                                {openSections.main ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                        </SidebarHeaderButton>
                    </ListItem>
                    <Collapse in={openSections.main} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            <ListItem disablePadding>
                                <StyledListItemButton
                                    component={NavLink}
                                    to={`chatbot/${chatbotIdentity}/dashboard`}
                                    sx={{ pl: 4 }}
                                    {...getLinkProps(true)}
                                    disabled={!isChatbotSetupComplete}
                                >
                                    <StyledListItemIcon>
                                        <DashboardIcons />
                                    </StyledListItemIcon>
                                    <StyledListItemText primary="Dashboard" />
                                </StyledListItemButton>
                            </ListItem>
                        </List>
                    </Collapse>

                    {/* Chat Space Section */}
                    <ListItem disablePadding>
                        <SidebarHeaderButton onClick={() => handleSectionToggle("chatspace")}>
                            <SidebarHeaderText>Chat Space</SidebarHeaderText>
                            <IconButton
                                edge="end"
                                size="small"
                                sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                            >
                                {openSections.chatspace ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                        </SidebarHeaderButton>
                    </ListItem>
                    <Collapse in={openSections.chatspace} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            <ListItem disablePadding>
                                <StyledListItemButton
                                    component={NavLink}
                                    to={`chatbot/${chatbotIdentity}/chat-space`}
                                    sx={{ pl: 4 }}
                                    {...getLinkProps(true)}
                                    disabled={!isChatbotSetupComplete}
                                >
                                    <StyledListItemIcon>
                                        <ForumIcon />
                                    </StyledListItemIcon>
                                    <StyledListItemText primary="Chat Space" />
                                </StyledListItemButton>
                            </ListItem>
                        </List>
                    </Collapse>

                    {/* User Management Section (Admin Only) */}
                    {userRole === "admin" && (
                        <>
                            <ListItem disablePadding>
                                <SidebarHeaderButton onClick={() => handleSectionToggle("users")}>
                                    <SidebarHeaderText>User Management</SidebarHeaderText>
                                    <IconButton 
                                        edge="end" 
                                        size="small"
                                        sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                                    >
                                        {openSections.users ? <ExpandLess /> : <ExpandMore />}
                                    </IconButton>
                                </SidebarHeaderButton>
                            </ListItem>
                            <Collapse in={openSections.users} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    <ListItem disablePadding>
                                        <StyledListItemButton
                                            component={NavLink}
                                            to={`/user-management/users`}
                                            sx={{ pl: 4 }}
                                            {...getLinkProps()}
                                            disabled={!isChatbotSetupComplete}
                                        >
                                            <StyledListItemIcon>
                                                <AccountCircleIcon />
                                            </StyledListItemIcon>
                                            <StyledListItemText primary="Users" />
                                        </StyledListItemButton>
                                    </ListItem>
                                    <ListItem disablePadding>
                                        <StyledListItemButton
                                            component={NavLink}
                                            to={`/user-management/roles`}
                                            sx={{ pl: 4 }}
                                            {...getLinkProps()}
                                            disabled={!isChatbotSetupComplete}
                                        >
                                            <StyledListItemIcon>
                                                <SettingsIcon />
                                            </StyledListItemIcon>
                                            <StyledListItemText primary="Roles" />
                                        </StyledListItemButton>
                                    </ListItem>
                                </List>
                            </Collapse>
                        </>
                    )}

                    {/* Loading State */}
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress 
                                size={24} 
                                sx={{ color: 'rgba(255, 255, 255, 0.7)' }} 
                            />
                        </Box>
                    )}

                    {/* Dynamic Modules */}
                    {!loading && modules.map(module => renderModuleSection(module))}
                </List>
                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                <UpgradePlan />
            </Box>
        </Drawer>
    );
}